import { Loader2, CheckCircle2, XCircle, Hotel } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HotelSearchProgress, SearchProgressLogEntry } from '@/types/hotel-search';

interface Props {
  municipality: string;
  progress: HotelSearchProgress;
  compact?: boolean;
}

export function DestinationEnrichmentProgress({ municipality, progress, compact = false }: Props) {
  const entries = progress.progress_log.filter(e => e.municipality === municipality);
  const done = entries.length;
  const isFailed = progress.status === 'failed';

  // Estimativa: total do lote quando só há um destino; senão usa done como mínimo
  const municipalitiesInLog = new Set(progress.progress_log.map(e => e.municipality));
  const estimatedTotal = municipalitiesInLog.size === 1 && progress.progress_total > 0
    ? progress.progress_total
    : Math.max(done, 1);

  const pct = estimatedTotal > 0 ? Math.round((done / estimatedTotal) * 100) : 0;
  const isFinalizing = progress.status === 'processing'
    && progress.progress_total > 0
    && progress.progress_done >= progress.progress_total;

  if (compact) {
    return (
      <div className={cn(
        'flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0',
        isFailed ? 'bg-red-50 border-red-100' : 'bg-teal-50/80 border-teal-200/60',
      )}>
        {isFailed ? (
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        ) : (
          <Loader2 className="w-4 h-4 text-voah-teal animate-spin flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-700 truncate">
            {isFailed ? 'Falha no enriquecimento' : isFinalizing ? 'Finalizando…' : `Enriquecendo ${municipality}…`}
          </p>
          <div className="mt-1.5 w-full bg-white/80 rounded-full h-1 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', isFailed ? 'bg-red-400' : 'bg-voah-teal')}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        </div>
        <span className="text-xs text-gray-500 tabular-nums flex-shrink-0">
          {done}{estimatedTotal > done ? `/${estimatedTotal}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-4">
          {isFailed ? (
            <XCircle className="w-5 h-5 text-red-500" />
          ) : (
            <Loader2 className="w-5 h-5 text-voah-teal animate-spin" />
          )}
          <h3 className="text-sm font-semibold text-gray-800">
            {isFailed ? 'Falha no enriquecimento' : isFinalizing ? 'Finalizando destino…' : `Enriquecendo ${municipality}`}
          </h3>
        </div>

        <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2 overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-500', isFailed ? 'bg-red-400' : 'bg-voah-teal')}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mb-6">
          {done} hotel{done !== 1 ? 'is' : ''} processado{done !== 1 ? 's' : ''}
          {estimatedTotal > done ? ` · ~${estimatedTotal} no lote` : ''}
        </p>

        {entries.length > 0 && (
          <div className="text-left space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-3 bg-white">
            {entries.slice(-8).reverse().map((h, i) => (
              <LogRow key={`${h.hotel_id}-${i}`} entry={h} />
            ))}
            {entries.length > 8 && (
              <p className="text-[10px] text-gray-400 pt-1">+ {entries.length - 8} anteriores</p>
            )}
          </div>
        )}

        {progress.status === 'processing' && !isFinalizing && (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mt-4">
            <Hotel className="w-3.5 h-3.5 text-voah-teal animate-pulse" />
            <span>Booking.com · outros destinos continuam disponíveis</span>
          </div>
        )}
      </div>
    </div>
  );
}

function LogRow({ entry }: { entry: SearchProgressLogEntry }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {entry.status === 'success' ? (
        <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
      ) : entry.status === 'unavailable' ? (
        <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
      ) : (
        <Loader2 className="w-3 h-3 text-gray-300 flex-shrink-0" />
      )}
      <span className={cn(entry.status === 'unavailable' && 'line-through text-gray-300')}>
        {entry.name || entry.hotel_id}
      </span>
    </div>
  );
}
